import { createContext, useContext, useState, useEffect, useCallback, type AnchorHTMLAttributes } from 'react'

interface RouterCtx {
  path: string
  navigate: (to: string) => void
}

const RouterContext = createContext<RouterCtx | null>(null)

/** Session-level scroll position cache keyed by pathname.
    Saved before each pushState navigation so the landing page can restore scroll position. */
const scrollCache: Record<string, number> = {}

/** Retrieve the last saved scroll position for a path. Returns undefined if never saved. */
// eslint-disable-next-line react-refresh/only-export-components
export function getSavedScrollPosition(path: string): number | undefined {
  return scrollCache[path]
}

export function RouterProvider({ children }: { children: React.ReactNode }) {
  const [path, setPath] = useState(() => window.location.pathname)

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname)
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const navigate = useCallback((to: string) => {
    scrollCache[window.location.pathname] = window.scrollY
    window.history.pushState({}, '', to)
    setPath(window.location.pathname)
    if (window.location.hash) {
      window.dispatchEvent(new HashChangeEvent('hashchange'))
    }
  }, [])

  return <RouterContext.Provider value={{ path, navigate }}>{children}</RouterContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useRouter() {
  const ctx = useContext(RouterContext)
  if (!ctx) throw new Error('useRouter must be inside RouterProvider')
  return ctx
}

export function Link({ href, children, className, onClick, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement>) {
  const { navigate } = useRouter()

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (onClick) onClick(e)
    if (e.defaultPrevented) return
    if (e.button !== 0) return
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return

    const url = new URL(href!, window.location.origin)
    if (url.origin !== window.location.origin) return

    const normalizePath = (p: string) => p || '/'
    const samePath = normalizePath(url.pathname) === normalizePath(window.location.pathname)
    if (samePath) return

    e.preventDefault()
    navigate(url.pathname + url.search + url.hash)
  }

  return <a href={href} onClick={handleClick} className={className} {...rest}>{children}</a>
}
